/**
 * OverviewTab — identity, status, location, contact, linked users.
 * Also absorbs the old Settings tab: customer change + danger zone delete.
 *
 * Identical layout between view and edit modes. Only the input type flips
 * via the shared Field wrapper.
 */

import { useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import CustomerForm from '../../../../../components/CustomerForm';
import EmailWithStatus from '../../../../../components/EmailWithStatus';
import SearchSelect from '../../../../../components/SearchSelect';
import Field from './Field';
import { PROPERTY_TYPES, TIER_CONFIG, STATUS_CONFIG, COUNTRY_OPTIONS, REGION_NAMES } from '../constants';
import { KNOWN_COUNTRIES } from '../../../../../lib/countries';
import type { EnrichedProperty, PropertyEditForm, PropertyMode } from './types';

/**
 * Map a free-text country name (as stored on customers.billing_address)
 * to the uppercase ISO code the property form's country dropdown uses.
 * Returns null if no match so the operator can still pick manually.
 */
function customerCountryToCode(name: string | null | undefined): string | null {
  if (!name) return null;
  const lower = name.trim().toLowerCase();
  const match = KNOWN_COUNTRIES.find((c) => c.label.toLowerCase() === lower);
  return match ? match.code : null;
}

interface OverviewTabProps {
  supabase: SupabaseClient;
  /** Null when creating a new property — the form drives values, no joined data exists yet. */
  property: EnrichedProperty | null;
  /**
   * Customer pre-loaded in create mode when the modal was opened from the
   * "Create POS Property" CTA. Used purely for displaying the linked-customer
   * name/email in the Linked Accounts row before the property is saved.
   */
  seededCustomer?: EnrichedProperty['customer'] | null;
  form: PropertyEditForm;
  setForm: (updater: (f: PropertyEditForm) => PropertyEditForm) => void;
  mode: PropertyMode;
  // Danger zone — only meaningful in edit mode with an existing property.
  deleting?: boolean;
  onDelete?: () => Promise<boolean>;
  onAfterDelete?: () => void;
  canDelete?: boolean;
}

export default function OverviewTab({
  supabase, property, seededCustomer, form, setForm, mode,
  deleting = false, onDelete, onAfterDelete, canDelete = false,
}: OverviewTabProps) {
  const [showCustomerPicker, setShowCustomerPicker] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteText, setDeleteText] = useState('');

  /**
   * The customer the operator picked or created from the modal in
   * create mode. Used so the Customer Gate banner sees the email
   * (and the linked-customer chip shows it) without having to
   * round-trip via the parent. Holds the same shape as the joined
   * EnrichedProperty['customer'] for drop-in fallback below.
   */
  const [pickedCustomer, setPickedCustomer] = useState<EnrichedProperty['customer']>(null);

  const provinceOptions: string[] = form.country
    ? Object.values(REGION_NAMES[form.country.toLowerCase()] || {})
    : [];
  const tierLabel = (TIER_CONFIG as Record<string, { label: string; frequency: string }>)[form.hospitality_tier]?.label || form.hospitality_tier;
  const statusLabel = (STATUS_CONFIG as Record<string, { label: string }>)[form.contract_status]?.label || form.contract_status;
  const typeLabel = PROPERTY_TYPES.find(t => t.value === form.property_type)?.label || form.property_type;

  // Prefer the joined customer on the property, falling back to a
  // seeded customer (orphan-POS CTA) or one picked in this session.
  // Only used for the create-mode customer link block; edit/view show
  // the full People block on Home.
  const linkedCustomer = property?.customer || pickedCustomer || seededCustomer || null;
  const customerNameDisplay = linkedCustomer
    ? linkedCustomer.company_name || linkedCustomer.customer_name || ''
    : '';

  /**
   * When the operator picks (or creates) a customer in the picker:
   *  - write customer_id into the form,
   *  - cache the customer's identity + email locally so the gate
   *    banner sees "Customer has email" without a round trip,
   *  - copy address / city / country / province from the customer's
   *    billing address into the form ONLY when the matching property
   *    field is still empty. The operator's typing is never trampled.
   */
  function onCustomerPicked(customer: Record<string, unknown>) {
    const c = customer as Record<string, unknown>;
    const customerId = (c.id as string) || '';
    if (!customerId) {
      setShowCustomerPicker(false);
      return;
    }

    setPickedCustomer({
      id: customerId,
      customer_name: (c.customer_name as string | null) ?? null,
      company_name: (c.company_name as string | null) ?? null,
      email: (c.email as string | null) ?? null,
      phone: (c.phone as string | null) ?? null,
      email_bouncing: (c.email_bouncing as boolean | null) ?? null,
      email_last_bounce_at: (c.email_last_bounce_at as string | null) ?? null,
    });

    const billing = (c.billing_address as Record<string, unknown> | null) ?? null;
    const billingAddress = (billing?.address as string | null) ?? null;
    const billingCity    = (billing?.city as string | null) ?? null;
    const billingCountry = customerCountryToCode(billing?.country as string | null);
    const billingState   = (billing?.state as string | null) ?? null;

    setForm((f) => ({
      ...f,
      customer_id: customerId,
      address_line1: f.address_line1?.trim() ? f.address_line1 : (billingAddress ?? f.address_line1),
      city:          f.city?.trim()          ? f.city          : (billingCity ?? f.city),
      country:       f.country?.trim()       ? f.country       : (billingCountry ?? f.country),
      province:      f.province?.trim()      ? f.province      : (billingState ?? f.province),
    }));
    setShowCustomerPicker(false);
  }

  async function confirmDelete() {
    if (!onDelete || !property || deleteText !== property.property_name) return;
    const ok = await onDelete();
    if (ok) {
      setShowDeleteConfirm(false);
      setDeleteText('');
      onAfterDelete?.();
    }
  }

  const showDangerZone = mode === 'edit' && canDelete && !!property && !!onDelete;

  // Customer Gate state in create mode. Lights the banner items as the
  // operator fills each requirement. Enforcement happens in handleCreate;
  // this is just visual feedback so users see what is needed before they
  // hit Save. Mirrors the SOP-POS-ONBOARDING.md Section 2 spec.
  const gateChecks = mode === 'create' ? {
    name: !!form.property_name.trim(),
    customer: !!form.customer_id,
    customerEmail: !!linkedCustomer?.email,
    address: !!form.address_line1?.trim(),
    city: !!form.city?.trim(),
    province: !!form.province?.trim(),
  } : null;
  const gateOk = gateChecks && Object.values(gateChecks).every(Boolean);

  return (
    <div className="flex flex-col gap-lg">
      {gateChecks && (
        <div
          style={{
            padding: '0.75rem 1rem',
            background: gateOk ? 'var(--color-success-bg)' : 'var(--color-warning-bg)',
            border: `1px solid ${gateOk ? '#bbf7d0' : '#fde68a'}`,
            borderRadius: 'var(--radius)',
            fontSize: 'var(--text-md)',
            color: gateOk ? 'var(--color-success-text)' : 'var(--color-warning-text)',
          }}
        >
          <div className="fw-600 mb-xs">
            {gateOk ? 'Ready to create' : 'Required to create this property'}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem 1rem', fontSize: 'var(--text-sm)' }}>
            <span>{gateChecks.name ? '✓' : '◻'} Property name</span>
            <span>{gateChecks.customer ? '✓' : '◻'} Linked customer</span>
            <span>{gateChecks.customerEmail ? '✓' : '◻'} Customer has email</span>
            <span>{gateChecks.address ? '✓' : '◻'} Street address</span>
            <span>{gateChecks.city ? '✓' : '◻'} City</span>
            <span>{gateChecks.province ? '✓' : '◻'} Province / region</span>
          </div>
        </div>
      )}

      {/* Identity */}
      <section>
        <h3 className="section-title-sm">Identity</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
          <Field
            label="Property name"
            mode={mode}
            required
            viewValue={form.property_name}
          >
            <input
              type="text"
              className="form-input"
              value={form.property_name}
              onChange={(e) => setForm((f) => ({ ...f, property_name: e.target.value }))}
            />
          </Field>
          <Field
            label="Code"
            mode={mode}
            viewValue={form.property_code}
          >
            <input
              type="text"
              className="form-input"
              value={form.property_code}
              maxLength={10}
              onChange={(e) => setForm((f) => ({ ...f, property_code: e.target.value.toUpperCase() }))}
            />
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
          <Field label="Category" mode={mode} viewValue={form.property_category}>
            <select className="form-input" value={form.property_category}
              onChange={(e) => setForm((f) => ({ ...f, property_category: e.target.value }))}>
              <option value="hospitality">Hospitality</option>
              <option value="community-living">Community Living</option>
            </select>
          </Field>
          <Field label="Property type" mode={mode} viewValue={typeLabel}>
            <select className="form-input" value={form.property_type}
              onChange={(e) => setForm((f) => ({ ...f, property_type: e.target.value }))}>
              {PROPERTY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Tier" mode={mode} viewValue={tierLabel}>
            <select className="form-input" value={form.hospitality_tier}
              onChange={(e) => setForm((f) => ({ ...f, hospitality_tier: e.target.value }))}>
              {Object.entries(TIER_CONFIG).map(([key, cfg]: [string, { label: string; frequency: string }]) => (
                <option key={key} value={key}>{cfg.label} ({cfg.frequency})</option>
              ))}
            </select>
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem' }}>
          <Field label="Contract status" mode={mode} viewValue={statusLabel}>
            <select className="form-input" value={form.contract_status}
              onChange={(e) => setForm((f) => ({ ...f, contract_status: e.target.value }))}>
              {Object.entries(STATUS_CONFIG).map(([key, cfg]: [string, { label: string }]) => (
                <option key={key} value={key}>{cfg.label}</option>
              ))}
            </select>
          </Field>
        </div>
      </section>

      {/* Capacity */}
      <section>
        <h3 className="section-title-sm">Capacity</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <Field label="Total rooms" mode={mode} viewValue={form.total_rooms}>
            <input type="number" className="form-input" min={0} value={form.total_rooms || ''}
              onChange={(e) => setForm((f) => ({ ...f, total_rooms: parseInt(e.target.value) || 0 }))} />
          </Field>
          <Field label="Total beds" mode={mode} viewValue={form.total_beds}>
            <input type="number" className="form-input" min={0} value={form.total_beds || ''}
              onChange={(e) => setForm((f) => ({ ...f, total_beds: parseInt(e.target.value) || 0 }))} />
          </Field>
        </div>
      </section>

      {/* Location */}
      <section>
        <h3 className="section-title-sm">Location</h3>
        <Field label="Address" mode={mode} viewValue={form.address_line1}>
          <textarea className="form-input" rows={2} value={form.address_line1}
            onChange={(e) => setForm((f) => ({ ...f, address_line1: e.target.value }))} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
          <Field label="City" mode={mode} viewValue={form.city}>
            <input type="text" className="form-input" value={form.city}
              onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} />
          </Field>
          <Field label="Country" mode={mode} viewValue={form.country}>
            <SearchSelect
              options={[{ value: '', label: 'Select country…' }, ...COUNTRY_OPTIONS.map(c => ({ value: c.value, label: c.label }))]}
              value={form.country}
              onChange={(v) => setForm((f) => ({ ...f, country: v, province: '' }))}
            />
          </Field>
          <Field label="Province / region" mode={mode} viewValue={form.province}>
            <SearchSelect
              disabled={!form.country}
              options={[{ value: '', label: 'Select province…' }, ...provinceOptions.map(name => ({ value: name, label: name }))]}
              value={form.province}
              onChange={(v) => setForm((f) => ({ ...f, province: v }))}
            />
          </Field>
        </div>
      </section>

      {/* Customer link — create mode only.
          In edit/view, the People block on the Home tab owns the
          linked customer + ambassador + manager. Showing them again
          here would duplicate. Ambassador + manager assignment in
          create mode is intentionally skipped; assign those after the
          property exists, from the Home tab. */}
      {mode === 'create' && (
        <section>
          <h3 className="section-title-sm">Customer</h3>
          <div style={{
            padding: '0.875rem 1rem',
            background: 'var(--color-bg, #f9fafb)',
            border: '1px solid var(--color-border, #e5e7eb)',
            borderRadius: 'var(--radius)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
          }}>
            <div className="flex-1">
              <div style={{ fontWeight: 600, fontSize: 'var(--text-lg)' }}>
                {customerNameDisplay || form.customer_id || 'No customer linked'}
                {linkedCustomer?.email ? (
                  <>
                    <span className="fw-400 text-muted">{' · '}</span>
                    <EmailWithStatus
                      email={linkedCustomer.email}
                      isBouncing={linkedCustomer.email_bouncing ?? undefined}
                      lastBounceAt={linkedCustomer.email_last_bounce_at}
                      className="fw-400"
                    />
                  </>
                ) : null}
              </div>
            </div>
            <button type="button" className="btn btn-secondary" onClick={() => setShowCustomerPicker(true)}>
              Link customer
            </button>
          </div>
        </section>
      )}

      {/* Danger zone — edit mode only, existing property only */}
      {showDangerZone && (
        <section>
          <h3 className="section-title-sm text-error">Danger zone</h3>
          {!showDeleteConfirm ? (
            <button type="button"
              className="btn btn-danger-outline"
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete property
            </button>
          ) : (
            <div style={{
              padding: '1rem',
              background: 'var(--color-error-bg)',
              borderRadius: 'var(--radius)',
              border: '1px solid #fecaca',
            }}>
              <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: 'var(--color-error)' }}>Confirm deletion</div>
              <p style={{ fontSize: 'var(--text-base)', marginBottom: '0.75rem', color: 'var(--color-error-text)' }}>
                This will permanently delete <strong>&ldquo;{property?.property_name}&rdquo;</strong>, all its
                rooms, and associated service requests. This action cannot be undone.
              </p>
              <div className="form-group mb-md">
                <label className="form-label" style={{ color: 'var(--color-error-text)', fontSize: 'var(--text-md)' }}>
                  Type <strong>{property?.property_name}</strong> to confirm:
                </label>
                <input type="text" className="form-input"
                  value={deleteText}
                  onChange={(e) => setDeleteText(e.target.value)}
                  placeholder={property?.property_name || ''}
                  style={{ borderColor: 'var(--color-error-bg)' }}
                  autoFocus
                />
              </div>
              <div className="flex gap-sm">
                <button type="button" className="btn btn-secondary"
                  onClick={() => { setShowDeleteConfirm(false); setDeleteText(''); }}
                  disabled={deleting}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger"
                  onClick={confirmDelete}
                  disabled={deleting || deleteText !== property?.property_name}
                >
                  {deleting ? 'Deleting…' : 'Delete property'}
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Customer picker sub-modal — only meaningful in create mode now
          (edit mode opens the picker from Home). */}
      {showCustomerPicker && mode === 'create' && (
        <div className="modal-overlay" onClick={() => setShowCustomerPicker(false)} style={{ zIndex: 1100 }}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Select customer</h2>
              <button className="modal-close" onClick={() => setShowCustomerPicker(false)}>&times;</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <CustomerForm
                supabase={supabase}
                onSave={onCustomerPicked}
                onCancel={() => setShowCustomerPicker(false)}
                globalSearchMode
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
